import { useState, useEffect, useMemo } from "react"
import { PRICE_GROUP_SWATCHES, splitSwatches, COLOR_FAMILIES } from "constants/catalogData"
import config from "config/app.config"

const SWATCH_API = `${config.API_URL}files/${config.APP_NAME}/category-swatches`
const SWATCH_KEY = config.APP_API_KEY

// Human-readable labels for price group codes
const PG_LABELS = {
  "1M": "1M - MFC Melamine 18mm",
  "2P": "2P - MFC + PVC Edge 18mm",
  "3S": "3S - Synchron Effect",
  "4C": "4C - Synchron 278cm",
  "5L": "5L - MDF Satin Lacquer 19mm",
  "6D": "6D - MDF Veneer Oak 20mm",
  "6L": "6L - MDF Lacquer Satin 19-22mm",
  "7G": "7G - MDF Gloss Lacquer 19mm",
  "7T": "7T - MDF Special Frames 22mm",
  "8D": "8D - Semi-Solid Oak 22mm",
  "8G": "8G - Semi-Solid Oak Gloss 22mm",
  "9H": "9H - Solid Oak Faltz 22mm",
  "9M": "9M - Solid Oak 22mm",
  "10A": "10A - Aluminum + Glass 20mm",
}

// Auth headers helper
const getApiHeaders = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser"))
  const token = authUser?.session_token || ""
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : "",
    "X-Api-Key": config.APP_API_KEY,
  }
}

// Fetch finish groups from DB
const fetchFinishGroupsFromDB = async () => {
  try {
    const response = await fetch(
      `${config.APP_URL}_table/finish_groups?filter=(is_active=true)&order=sort_order ASC`,
      { headers: getApiHeaders() }
    )
    const data = await response.json()
    const groups = data.resource || []
    if (groups.length === 0) return null
    return groups.map(g => ({
      code: g.code,
      label: `${g.code} - ${g.name}`,
      ...g,
    }))
  } catch {
    return null
  }
}

// Fetch swatches for a finish group from DB
const fetchSwatchesFromDB = async (finishGroupId) => {
  try {
    const response = await fetch(
      `${config.APP_URL}_table/finish_options?filter=(finish_group_id=${finishGroupId}) AND (is_active=true)&order=sort_order ASC`,
      { headers: getApiHeaders() }
    )
    const data = await response.json()
    const options = data.resource || []
    if (options.length === 0) return null
    return options.map(o => {
      // DB stores relative paths like /swatches/1M/file.webp
      // Resolve to full file API URL so images actually load
      let url = o.swatch_url || ""
      if (url && !url.startsWith("http")) {
        const match = url.match(/\/swatches\/(.+)/)
        url = match
          ? `${SWATCH_API}/${match[1]}?api_key=${SWATCH_KEY}&v=2`
          : url
      }
      return {
        label: o.name,
        swatchUrl: url,
        hexColor: o.hex_color || null,
        finishOptionId: o.id,
      }
    })
  } catch {
    return null
  }
}

/**
 * Dynamic catalog hook — simplified.
 *
 * Now only responsible for loading swatches by model code.
 * Category tree and price group options are no longer needed
 * (replaced by useCategoryOptions hook and DB categories).
 */
export const useDynamicCatalog = () => {
  const [swatchesByGroup, setSwatchesByGroup] = useState({})
  const [finishGroupsMap, setFinishGroupsMap] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadSwatches()
  }, [])

  const loadSwatches = async () => {
    try {
      // Try DB finish groups first
      const dbFinishGroups = await fetchFinishGroupsFromDB()

      let pgOpts
      const fgMap = {}

      if (dbFinishGroups && dbFinishGroups.length > 0) {
        pgOpts = dbFinishGroups
        for (const g of dbFinishGroups) {
          if (g.id) fgMap[g.id] = g.code
        }
        console.info(`[Catalog] Using ${dbFinishGroups.length} finish groups from DB`)
      } else {
        // Fallback to static PG_LABELS
        const allCodes = Object.keys(PG_LABELS).sort((a, b) => {
          const numA = parseInt(a) || 99
          const numB = parseInt(b) || 99
          return numA - numB || a.localeCompare(b)
        })
        pgOpts = allCodes.map((code) => ({
          code,
          label: PG_LABELS[code] || code,
        }))
        console.info(`[Catalog] Using ${pgOpts.length} legacy price groups`)
      }

      setFinishGroupsMap(fgMap)

      // Build swatches per group
      const swatches = {}
      const getHeaders = () => {
        const authUser = JSON.parse(localStorage.getItem("authUser"))
        const token = authUser?.session_token || ""
        return {
          Accept: "application/json",
          Authorization: token ? `Bearer ${token}` : "",
          "X-Flexflow-Api-Key": SWATCH_KEY,
        }
      }

      await Promise.all(
        pgOpts.map(async (pg) => {
          const pgCode = pg.code

          // Always use file-based swatch API for images (DB swatch_url paths are stale)
          try {
            const res = await fetch(
              `${SWATCH_API}/${pgCode}/?api_key=${SWATCH_KEY}&_t=${Date.now()}`,
              { headers: getHeaders() }
            )
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            const data = await res.json()
            const files = (data.resource || []).filter(f => f.type === "file")
            swatches[pgCode] = files.map(f => ({
              label: f.name.replace(/\.[^.]+$/, "").replace(/-/g, " "),
              swatchUrl: `${SWATCH_API}/${pgCode}/${f.name}?api_key=${SWATCH_KEY}&v=2`,
            }))
          } catch {
            // Final fallback to static data — use legacy flat list for back-compat
            const pgData = PRICE_GROUP_SWATCHES[pgCode]
            swatches[pgCode] = pgData?.swatches || []
          }
        })
      )

      // Mirror every box family's swatches onto its sibling Special / extra-box codes.
      // This is what makes the swatch system "truly shared": editing 5L's storage
      // folder auto-updates E5, 6L, 7T, etc. — they all share the same array ref.
      for (const fam of Object.values(COLOR_FAMILIES)) {
        const canonical = swatches[fam.canonical]
        if (!canonical) continue
        for (const code of [...fam.boxes, ...fam.specials]) {
          if (code === fam.canonical) continue
          swatches[code] = canonical
        }
      }

      setSwatchesByGroup(swatches)
      setError(null)

      console.info(
        `[Catalog] ${pgOpts.length} groups, ` +
        `${Object.values(swatches).reduce((s, a) => s + (a?.length || 0), 0)} swatches loaded`
      )
    } catch (err) {
      console.warn("Dynamic catalog failed:", err.message)
      setError(err.message)

      // Fallback swatches (flat list — back-compat)
      const fallbackSwatches = {}
      for (const code of Object.keys(PG_LABELS)) {
        const pgData = PRICE_GROUP_SWATCHES[code]
        fallbackSwatches[code] = pgData?.swatches || []
      }
      setSwatchesByGroup(fallbackSwatches)
    } finally {
      setLoading(false)
    }
  }

  // Derived: split each group's flat list into { colors, models } once per change.
  // Consumers picking only colors (or only door models) read from these.
  const { colorsByGroup, modelsByGroup } = useMemo(() => {
    const colors = {}
    const models = {}
    for (const [code, entries] of Object.entries(swatchesByGroup)) {
      const split = splitSwatches(entries)
      colors[code] = split.colors
      models[code] = split.models
    }
    return { colorsByGroup: colors, modelsByGroup: models }
  }, [swatchesByGroup])

  return { swatchesByGroup, colorsByGroup, modelsByGroup, finishGroupsMap, loading, error }
}
